import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ChatAppRequest } from "@/api/models"

import { VectorFieldOptions } from "@/api/models"
import { useM365ApprovalGate } from "@/features/ai-explorer/hooks/use-m365-approval-gate"
import { useLibrariesSelection } from "@/features/navigator/hooks/use-libraries-selection"
import { useStyleTemplateChatStore } from "@/features/studio-ai/stores/style-template-chat-store"
import { useStyleTemplateEditorStore } from "@/features/studio-ai/stores/style-template-editor-store"
import { useTrustedSourcesSelection } from "@/features/trusted-sources/hooks/use-trusted-sources-selection"
import {
  useAgentModeEnabled,
  useHighReasoningEnabled,
  useM365Enabled,
  useNetboxEnabled,
  usePromptsEnabled,
  useServiceDeskEnabled,
  useWebSearchEnabled,
} from "@/hooks/use-route-overrides"
import { usePreferencesStore } from "@/stores/preferences-store"

import { useStreamingChat } from "./use-streaming-chat"

export type { StreamingChatMessage as StyleTemplateChatMessage } from "./use-streaming-chat"

type ExtractedStyleData = {
  title?: string
  description?: string
  prompt_fragment?: string
  brand_colors?: string[]
  media_type?: string
}

type UseStyleTemplateChatOptions = {
  onExtractedData?: (data: ExtractedStyleData) => void
}

const STYLE_TEMPLATE_SESSION_TYPE = "studio-style-template"

function extractJsonFromResponse(content: string): ExtractedStyleData | null {
  const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/)
  if (!jsonMatch) return null

  try {
    return JSON.parse(jsonMatch[1]) as ExtractedStyleData
  } catch {
    return null
  }
}

export function useStyleTemplateChat({ onExtractedData }: UseStyleTemplateChatOptions = {}) {
  const [files, setFiles] = useState<File[]>([])

  const enableAgent = useStyleTemplateChatStore((state) => state.enableAgent)
  const setEnableAgent = useStyleTemplateChatStore((state) => state.setEnableAgent)
  const enableWebSearch = useStyleTemplateChatStore((state) => state.enableWebSearch)
  const setEnableWebSearch = useStyleTemplateChatStore((state) => state.setEnableWebSearch)
  const enableM365 = useStyleTemplateChatStore((state) => state.enableM365)
  const setEnableM365 = useStyleTemplateChatStore((state) => state.setEnableM365)
  const enableServiceDesk = useStyleTemplateChatStore((state) => state.enableServiceDesk)
  const setEnableServiceDesk = useStyleTemplateChatStore((state) => state.setEnableServiceDesk)
  const enableNetbox = useStyleTemplateChatStore((state) => state.enableNetbox)
  const setEnableNetbox = useStyleTemplateChatStore((state) => state.setEnableNetbox)
  const enableHighReasoning = useStyleTemplateChatStore((state) => state.enableHighReasoning)
  const setEnableHighReasoning = useStyleTemplateChatStore((state) => state.setEnableHighReasoning)
  const hasInitializedDefaults = useStyleTemplateChatStore((state) => state.hasInitializedDefaults)
  const setHasInitializedDefaults = useStyleTemplateChatStore(
    (state) => state.setHasInitializedDefaults
  )
  const resetToolState = useStyleTemplateChatStore((state) => state.reset)

  const showWebSearchOption = useWebSearchEnabled()
  const showM365Option = useM365Enabled()
  const showServiceDeskOption = useServiceDeskEnabled()
  const showNetboxOption = useNetboxEnabled()
  const hasAgentModeFeature = useAgentModeEnabled()
  const showHighReasoning = useHighReasoningEnabled()
  const isPromptsFeatureEnabled = usePromptsEnabled()
  const { activeLibraryIds } = useLibrariesSelection(STYLE_TEMPLATE_SESSION_TYPE)
  const { selectedUrls, trustedMode } = useTrustedSourcesSelection(STYLE_TEMPLATE_SESSION_TYPE)
  const { approveM365, declineM365, hasM365Approval, hasDeclinedM365, isM365ApprovalLoading } =
    useM365ApprovalGate()
  const [showM365Info, setShowM365Info] = useState(false)
  const openPreferencesWith = usePreferencesStore((s) => s.openWith)
  const { t: tStyleTemplate } = useTranslation()

  const editorTitle = useStyleTemplateEditorStore((state) => state.title)
  const editorDescription = useStyleTemplateEditorStore((state) => state.description)
  const editorPromptFragment = useStyleTemplateEditorStore((state) => state.promptFragment)
  const editorBrandColors = useStyleTemplateEditorStore((state) => state.brandColors)

  useEffect(() => {
    if (hasInitializedDefaults) return
    if (isM365ApprovalLoading) return
    resetToolState({
      enableWebSearch: showWebSearchOption,
      enableM365: showM365Option && hasM365Approval,
      enableServiceDesk: showServiceDeskOption,
      enableNetbox: showNetboxOption,
    })
    setHasInitializedDefaults(true)
  }, [
    hasInitializedDefaults,
    hasM365Approval,
    isM365ApprovalLoading,
    resetToolState,
    setHasInitializedDefaults,
    showM365Option,
    showNetboxOption,
    showServiceDeskOption,
    showWebSearchOption,
  ])

  const buildRequest = useCallback(
    (historyForModel: ChatAppRequest["messages"]): ChatAppRequest => {
      return {
        messages: historyForModel,
        context: {
          overrides: {
            agent_name: "style_template_creator",
            use_custom_gpt: true,
            custom_gpt_model: "auto",
            use_web_search: showWebSearchOption ? enableWebSearch : false,
            use_m365: showM365Option && hasM365Approval ? enableM365 : false,
            use_service_desk: showServiceDeskOption ? enableServiceDesk : false,
            use_netbox: showNetboxOption ? enableNetbox : false,
            use_agent_mode: hasAgentModeFeature ? enableAgent : false,
            reasoning_effort: showHighReasoning && enableHighReasoning ? "high" : "medium",
            vector_fields: [VectorFieldOptions.Embedding],
            search_services: activeLibraryIds,
            source_urls: selectedUrls,
            trusted_mode: trustedMode,
            style_template_title: editorTitle || undefined,
            style_template_description: editorDescription || undefined,
            style_template_prompt_fragment: editorPromptFragment || undefined,
            style_template_brand_colors:
              editorBrandColors.length > 0 ? editorBrandColors : undefined,
          },
        },
        stream: true,
        session_state: null,
      }
    },
    [
      editorTitle,
      editorDescription,
      editorPromptFragment,
      editorBrandColors,
      showWebSearchOption,
      enableWebSearch,
      showM365Option,
      hasM365Approval,
      enableM365,
      showServiceDeskOption,
      enableServiceDesk,
      showNetboxOption,
      enableNetbox,
      hasAgentModeFeature,
      enableAgent,
      showHighReasoning,
      enableHighReasoning,
      activeLibraryIds,
      selectedUrls,
      trustedMode,
    ]
  )

  const handleResponseComplete = useCallback(
    (content: string) => {
      const extracted = extractJsonFromResponse(content)
      if (extracted && onExtractedData) {
        onExtractedData(extracted)
      }
    },
    [onExtractedData]
  )

  const { messages, isStreaming, streamingContent, error, sendMessage, cancelStream } =
    useStreamingChat({
      useAgentMode: enableAgent,
      buildRequest,
      files,
      onResponseComplete: handleResponseComplete,
    })

  const addFiles = useCallback((newFiles: File[]) => {
    setFiles((prev) => [...prev, ...newFiles])
  }, [])

  const removeFile = useCallback((file: File | string) => {
    setFiles((prev) =>
      prev.filter((f) => (typeof file === "string" ? f.name !== file : f !== file))
    )
  }, [])

  const handleM365Change = useCallback(
    (enabled: boolean) => {
      if (enabled && !hasM365Approval) {
        if (hasDeclinedM365) {
          toast.info(
            tStyleTemplate("m365_tool.toast.declined_in_settings", {
              defaultValue: "Microsoft 365 access is off — turn it on in Preferences.",
            })
          )
          openPreferencesWith("general")
          return
        }
        setShowM365Info(true)
        return
      }
      setEnableM365(enabled)
    },
    [hasM365Approval, hasDeclinedM365, openPreferencesWith, setEnableM365, tStyleTemplate]
  )

  const handleM365Approve = useCallback(() => {
    approveM365()
    setEnableM365(true)
    setShowM365Info(false)
  }, [approveM365, setEnableM365])

  const handleM365Decline = useCallback(() => {
    declineM365()
    setEnableM365(false)
    setShowM365Info(false)
  }, [declineM365, setEnableM365])

  const handleM365Dismiss = useCallback(() => {
    setEnableM365(false)
    setShowM365Info(false)
  }, [setEnableM365])

  return {
    sessionType: STYLE_TEMPLATE_SESSION_TYPE,
    messages,
    isStreaming,
    streamingContent,
    files,
    error,
    showWebSearchOption,
    showM365Option,
    showServiceDeskOption,
    showNetboxOption,
    hasAgentModeFeature,
    showHighReasoning,
    isPromptsFeatureEnabled,
    enableAgent,
    enableWebSearch,
    enableM365,
    enableServiceDesk,
    enableNetbox,
    enableHighReasoning,
    setEnableAgent,
    setEnableWebSearch,
    setEnableM365: handleM365Change,
    setEnableServiceDesk,
    setEnableNetbox,
    setEnableHighReasoning,
    showM365Info,
    handleM365Approve,
    handleM365Decline,
    handleM365Dismiss,
    sendMessage,
    cancelStream,
    addFiles,
    removeFile,
  }
}
